import { Injectable } from '@angular/core';
import { signal, WritableSignal } from '@angular/core';

interface PreloadedVideo {
  url: string;
  blobUrl: string;
}

/**
 * Servicio para precargar archivos de video WebM.
 * Descarga los videos y los almacena como Object URLs para un acceso instantáneo,
 * reduciendo la latencia de carga en componentes críticos.
 */
@Injectable({
  providedIn: 'root',
})
export class WebMPreloadService {
  // Usamos una signal para almacenar los videos precargados.
  // Esto permite reactividad si en el futuro quisiéramos observar cambios.
  private preloadedVideos: WritableSignal<Map<string, string>> = signal(new Map());

  /**
   * Descarga una lista de URLs de video y las almacena en el servicio.
   * @param urls - Un array de strings con las rutas a los archivos .webm.
   */
  async preload(urls: string[]): Promise<void> {
    const videoPromises = urls.map(async (url) => {
      // Evita volver a descargar si ya existe.
      if (this.preloadedVideos().has(url)) {
        return;
      }

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Error al descargar el video: ${response.statusText}`);
        }
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        // Actualizamos la signal con el nuevo video precargado.
        this.preloadedVideos.update(map => {
          map.set(url, blobUrl);
          return map;
        });
      } catch (error) {
        console.error(`[WebMPreloadService] Falló la precarga de ${url}:`, error);
      }
    });

    await Promise.all(videoPromises);
  }

  /**
   * Obtiene la URL de un video precargado.
   * @param url - La ruta original del archivo .webm.
   * @returns La Object URL del blob si está precargado, o undefined.
   */
  getPreloadedUrl(url: string): string | undefined {
    return this.preloadedVideos().get(url);
  }

  /**
   * Libera la memoria de un Object URL cuando ya no se necesita.
   * Es crucial llamar a este método para evitar memory leaks.
   * @param url - La ruta original del archivo .webm cuya Object URL será revocada.
   */
  revokeUrl(url: string): void {
    this.preloadedVideos.update(map => {
      const blobUrl = map.get(url);
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        map.delete(url);
      }
      return map;
    });
  }
}